/**
 * Composable para sincronização de relógio com o servidor.
 * 
 * Busca a hora do servidor uma única vez e calcula o offset em relação
 * ao relógio local. A partir daí, atualiza o display a cada segundo
 * usando o offset fixo, evitando "pulos" no horário.
 * 
 * Em caso de falha na sincronização, usa a hora local como fallback
 * com indicação visual de que não foi validada pelo servidor.
 * 
 * Compatível com Vue 2 - retorna objeto reativo simples
 * 
 * @param {Object} options - Opções de configuração
 * @param {number} options.timeout - Timeout em ms para requisição (default: 5000)
 * @returns {Object} Estado reativo e métodos de controle
 */
export function useServerClock(options = {}) {
  const { timeout = 5000 } = options

  // Estado (objeto simples, será reativo quando usado em data() do componente)
  const state = {
    date: '',
    time: '',
    timezone: '',
    status: 'loading', // 'loading' | 'synced' | 'local' | 'error'
    isRunning: false
  }

  // Interno
  let offset = 0
  let intervalId = null

  /**
   * Retorna label do status
   */
  function getStatusLabel() {
    switch (state.status) {
      case 'synced':
        return 'Relógio sincronizado'
      case 'local':
        return 'Hora do dispositivo (não validada)'
      case 'loading':
        return 'Carregando...'
      case 'error':
        return 'Erro ao sincronizar'
      default:
        return ''
    }
  }

  /**
   * Formata data no padrão brasileiro DD/MM/YYYY
   */
  function formatDate(dateObj) {
    const day = String(dateObj.getDate()).padStart(2, '0')
    const month = String(dateObj.getMonth() + 1).padStart(2, '0')
    const year = dateObj.getFullYear()
    return `${day}/${month}/${year}`
  }

  /**
   * Formata hora no padrão HH:MM:SS
   */
  function formatTime(dateObj) {
    const hours = String(dateObj.getHours()).padStart(2, '0')
    const minutes = String(dateObj.getMinutes()).padStart(2, '0')
    const seconds = String(dateObj.getSeconds()).padStart(2, '0')
    return `${hours}:${minutes}:${seconds}`
  }

  /**
   * Atualiza os valores de data/hora com base no offset calculado
   */
  function updateDisplay() {
    const now = new Date(Date.now() + offset)
    state.date = formatDate(now)
    state.time = formatTime(now)
  }

  /**
   * Busca hora do servidor e calcula offset
   */
  async function syncWithServer() {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const apiUrl = process.env.VUE_APP_ROOT_API || ''
      const response = await fetch(`${apiUrl}server-time`, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
        }
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()

      // Calcula offset entre servidor e navegador
      const serverTime = data.timestamp * 1000
      const localTime = Date.now()
      offset = serverTime - localTime

      // Atualiza timezone do servidor
      state.timezone = data.timezone || 'America/Sao_Paulo'
      state.status = 'synced'

      return true
    } catch (error) {
      clearTimeout(timeoutId)
      
      // Fallback para hora local
      offset = 0
      state.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local'
      state.status = 'local'

      return false
    }
  }

  /**
   * Inicia o relógio
   */
  async function start() {
    if (state.isRunning) {
      return
    }

    state.isRunning = true
    state.status = 'loading'

    // Sincroniza com servidor
    await syncWithServer()

    // Atualiza display imediatamente
    updateDisplay()

    // Inicia interval de 1 segundo
    intervalId = setInterval(() => {
      updateDisplay()
    }, 1000)
  }

  /**
   * Para o relógio e limpa o interval
   */
  function stop() {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    state.isRunning = false
  }

  /**
   * Reinicia sincronização com servidor
   */
  async function refresh() {
    stop()
    await start()
  }

  return {
    // Estado
    state,
    getStatusLabel,

    // Métodos
    start,
    stop,
    refresh
  }
}

export default useServerClock
